import { useMyUser } from '@consigli/hooks';
import { type Project, ProjectRole } from '@consigli/types';
import { useMemo } from 'react';

/**
 * Whether the signed-in user may change or delete a given project.
 *
 * The rule is the one the project management list already applies inline:
 * superuser and org admin manage everything, everyone else manages only the
 * projects they are admin of.
 *
 * That list is deliberately not refactored onto this hook. It has no tests, and
 * its own version answers a different question — a Set of manageable ids, with
 * `null` meaning "all", which three call sites use to decide the pagination
 * count. Rewriting that blind to save a duplicated condition is a poor trade.
 * The duplication is recorded rather than accidental: if the rule changes, both
 * this and `project-management-list.tsx` have to change.
 *
 * It mirrors the backend's grant: `create_project_admin_group` assigns
 * `change_project_object` and `delete_project_object` to the project's *admin*
 * group, and `create_project_user_group` assigns only `view_project_object`.
 * The org-admin and superuser arms mirror `ProjectViewSet.get_queryset`, which
 * widens an org admin's queryset to their whole organisation.
 *
 * **This is the only gate.** `ProjectViewSet` declares no `permission_classes`,
 * so DRF falls back to `IsAuthenticated` and the object permissions above are
 * never checked on `PATCH` or `DELETE` — `get_queryset`, built from *view*, is
 * what the request actually passes through. A plain member's edit would
 * therefore succeed if the UI let them make it. Raised separately; until it is
 * fixed, treat this hook as load-bearing rather than cosmetic.
 */
export const useCanManageProject = (projectId: Project['id'] | undefined): boolean => {
  const { user } = useMyUser();

  return useMemo(() => {
    if (user.isSuperuser || user.isOrgAdmin) return true;
    if (projectId === undefined) return false;

    return user.projectAccess.some(
      (access) => access.id === projectId && access.role === ProjectRole.ADMIN,
    );
  }, [projectId, user.isOrgAdmin, user.isSuperuser, user.projectAccess]);
};
